// API Configuration
const isDevelopment = process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

// Get API base URL from environment variable or fallback to defaults
const getApiBaseUrl = () => {
  // Check for Vercel environment variable first
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  // Fallback to development/production logic
  return isDevelopment
    ? 'http://127.0.0.1:8001/api'  // Local development (port 8001)
    : 'https://api.wallst.co.il/api';  // Production - using correct subdomain
};

export const API_BASE = getApiBaseUrl();

// API keys — read from env vars, fallback for dev
export const API_KEY = process.env.REACT_APP_API_KEY || 'psagot-api-key-2024-secure';
export const ADMIN_API_KEY = process.env.REACT_APP_ADMIN_API_KEY || API_KEY;

// Detect Safari mobile
const isSafariMobile = (): boolean => {
  const ua = navigator.userAgent.toLowerCase();
  return /safari/.test(ua) && /iphone|ipad|ipod/.test(ua) && !/chrome/.test(ua);
};

// Helper function to handle API requests with Safari mobile retry logic
export const apiRequest = async (endpoint: string, options: RequestInit = {}, retries = 2) => {
  const url = `${API_BASE}${endpoint}`;

  const makeRequest = async (): Promise<any> => {
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': API_KEY,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  };

  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await makeRequest();
    } catch (error: any) {
      lastError = error;
      console.error(`API Request Error (${url}) attempt ${attempt + 1}:`, error);

      // Check if it's a Safari mobile "Load failed" error (happens when app goes to background)
      const isLoadFailed = error instanceof TypeError &&
        (error.message.includes('Load failed') || error.message.includes('Failed to fetch'));

      if (isLoadFailed && isSafariMobile() && attempt < retries) {
        // Wait a moment for the network to reconnect after returning from background
        console.log(`Safari mobile load failed, retrying in 1s (attempt ${attempt + 2}/${retries + 1})...`);
        await new Promise(resolve => setTimeout(resolve, 1000));
        continue;
      }

      // Handle mixed content errors specifically
      if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
        if (window.location.protocol === 'https:' && url.startsWith('http:')) {
          throw new Error('Mixed Content Error: HTTPS page cannot load HTTP resources. Please configure HTTPS for your backend.');
        }
      }

      // Don't retry for other errors
      break;
    }
  }

  throw lastError;
};

export const API_ENDPOINTS = {
  // Document endpoints
  documents: `${API_BASE}/documents`,
  document: (id: number) => `${API_BASE}/documents/${id}`,
  
  // PDF AI Chat endpoints
  pdfSuggestions: `${API_BASE}/documents/pdf-suggestions`,
  pdfQuestion: `${API_BASE}/documents/pdf-question`,
  pdfSummary: `${API_BASE}/documents/pdf-summary`,
  
  // Direct PDF AI Chat endpoints (using Gemini's PDF reading)
  pdfDirectSuggestions: `${API_BASE}/documents/pdf-direct-suggestions`,
  pdfDirectQuestion: `${API_BASE}/documents/pdf-direct-question`,
  pdfDirectSummary: `${API_BASE}/documents/pdf-direct-summary`,
  
  // Audio endpoints
  audioSuggestions: `${API_BASE}/documents/audio-suggestions`,
  
  // CLSA endpoints
  clsaDocuments: `${API_BASE}/clsa/documents`,
  clsaDocument: (id: number) => `${API_BASE}/clsa/documents/${id}`,
  clsaPdfSuggestions: `${API_BASE}/clsa/documents/pdf-suggestions`,
  clsaPdfQuestion: `${API_BASE}/clsa/documents/pdf-question`,
  clsaPdfSummary: `${API_BASE}/clsa/documents/pdf-summary`,
  clsaPdfDirectSuggestions: `${API_BASE}/clsa/documents/pdf-direct-suggestions`,
  clsaPdfDirectQuestion: `${API_BASE}/clsa/documents/pdf-direct-question`,
  clsaPdfDirectSummary: `${API_BASE}/clsa/documents/pdf-direct-summary`,
  clsaAudioSuggestions: `${API_BASE}/clsa/documents/audio-suggestions`,
  
  // General endpoints
  ask: `${API_BASE}/ask`,
  ragAsk: `${API_BASE}/rag/ask`,
  health: `${API_BASE}/health`,
  status: `${API_BASE}/status`,
  
  // Scraper endpoints
  scraperStart: `${API_BASE}/scraper/start`,
  scraperStatus: `${API_BASE}/scraper/status`,
  
  // V3 RAG endpoints (new enhanced RAG system)
  v3Query: `${API_BASE}/v3/query`,
  v3QueryStream: `${API_BASE}/v3/query/stream`,  // SSE streaming endpoint
  v3Search: `${API_BASE}/v3/search`,
  v3Documents: `${API_BASE}/v3/documents`,
  v3Stats: `${API_BASE}/v3/stats`,
  v3Health: `${API_BASE}/v3/health`,
  v3Ingest: `${API_BASE}/v3/ingest`,
  v3QuickQA: `${API_BASE}/v3/quick-qa`,

  // Sarah - Agentic AI Assistant
  sarah: `${API_BASE}/v3/sarah`,
  sarahStream: `${API_BASE}/v3/sarah/stream`,

  // Server logs (debug)
  serverLogs: `${API_BASE}/v3/server-logs`,

  // Portal Monitor
  monitorDocuments: `${API_BASE}/monitor/documents`,
  monitorScan: `${API_BASE}/monitor/scan`,
  monitorIngest: `${API_BASE}/monitor/ingest`,
  monitorStats: `${API_BASE}/monitor/stats`,
} as const;